import { Rack6slotModel } from "./modelFunc/Orion_6slotrack";

export const Rack6slot = {
  name: "6-slot",
  slots: ["0A", "0B", "5", "6", "7", "8"],
  model: Rack6slotModel,
  rules: {
    "0A": ["powernom"],
    "0B": ["powernom"],
    "5": ["supervisor"],
    "6": ["supervisor"],
    "7": ["usPlus", "versaldhu"],
    "8": ["usPlus", "versaldhu"],
  },
    canPlace(moduleType, slotName) {
        const allowed = this.rules[slotName] || this.rules.default;
        return allowed.includes(moduleType);
    },

    /**
     * Finds the best available slot for a given module type.
     * This is a helper function for the more complex addModule method.
     */
    assignSlot(moduleType, modules) {
        // 1) Power modules → only 0A, 0B
        if (moduleType === "powernom") {
            for (let slot of ["0A", "0B"]) {
                if (this.slots.includes(slot) && !modules[slot]) return slot;
            }
            return null;
        }

        // 2) Supervisor modules → only 5, 6
        if (moduleType === "supervisor") {
            for (let slot of ["5", "6"]) {
                if (this.slots.includes(slot) && !modules[slot]) return slot;
            }
            return null;
        }

        // 3) Usplus/Versal → prefer 7,8 then fallback
        if (moduleType === "usPlus" || moduleType === "versaldhu") {
            for (let slot of ["7", "8"]) {
                if (this.slots.includes(slot) && !modules[slot]) return slot;
            }
            // fallback: other valid slots
            for (let slot of this.slots) {
                if (!modules[slot] && this.canPlace(moduleType, slot)) {
                    return slot;
                }
            }
            return null;
        }

        // 4) General modules → any slot except reserved ones
        for (let slot of this.slots) {
            if (!modules[slot] && !["0A", "0B", "5", "6", "7", "8"].includes(slot)) {
                return slot;
            }
        }
        return null; // no free slot found
    },

    /**
     * Adds a module and recalculates positions based on priority rules.
     * Returns the new modules object, or null if placement is not possible.
     */
    addModule(module, modules) {
        let updatedModules = { ...modules };
        const moduleType = module.id;

        // Handle usPlus with priority
        if (moduleType === 'usPlus') {
            const prioritySlots = ['7', '8'];

            // Try to place in an empty priority slot
            for (const slot of prioritySlots) {
                if (!updatedModules[slot]) {
                    updatedModules[slot] = module;
                    return updatedModules;
                }
            }

            // Try to displace a versal from a priority slot
            for (const slot of prioritySlots) {
                if (updatedModules[slot]?.id === 'versaldhu') {
                    const versalToMove = updatedModules[slot];
                    const tempModulesForSearch = { ...updatedModules, [slot]: module };
                    
                    const newSlotForVersal = this.assignSlot('versaldhu', tempModulesForSearch);

                    if (newSlotForVersal) {
                        updatedModules[newSlotForVersal] = versalToMove;
                        updatedModules[slot] = module;
                        return updatedModules;
                    }
                }
            }
        }

        // Default behavior for all other modules (and usPlus/versal fallback)
        const slot = this.assignSlot(moduleType, updatedModules);
        if (slot) {
            updatedModules[slot] = module;
            return updatedModules;
        }

        return null; // Indicates no slot was found
    },

    /**
     * Removes a module and recalculates positions to fill priority slots.
     * Returns the new modules object.
     */
    removeModule(slotToRemove, modules) {
        let updatedModules = { ...modules };
        const removedModule = updatedModules[slotToRemove];
        updatedModules[slotToRemove] = null;

        if (!removedModule) return updatedModules;

        const prioritySlots = ['7', '8'];

        // If a usPlus was removed from slot 7, and a versal is in slot 8, move versal to 7.
        if (slotToRemove === '7' && updatedModules['8']?.id === 'versaldhu') {
            updatedModules['7'] = updatedModules['8'];
            updatedModules['8'] = null;
            return updatedModules;
        }

        // If a priority slot was freed, check if a module from a non-priority slot can be moved up.
        if (prioritySlots.includes(slotToRemove)) {
            let moduleToMove = null;
            let originalSlot = null;

            // Prioritize moving a usPlus first
            for (const slot of this.slots) {
                if (!prioritySlots.includes(slot) && updatedModules[slot]?.id === 'usPlus') {
                    moduleToMove = updatedModules[slot];
                    originalSlot = slot;
                    break;
                }
            }
            // If no usPlus, look for a versal
            if (!moduleToMove) {
                for (const slot of this.slots) {
                    if (!prioritySlots.includes(slot) && updatedModules[slot]?.id === 'versaldhu') {
                        moduleToMove = updatedModules[slot];
                        originalSlot = slot;
                        break;
                    }
                }
            }

            if (moduleToMove) {
                updatedModules[slotToRemove] = moduleToMove;
                updatedModules[originalSlot] = null;
            }
        }

        return updatedModules;
    }
};